import { resolveThumbnailUrl } from '@travel-planner/shared/utils';
import {
  getVideoEmbedUrl,
  isDirectMediaUrl,
  narrowVideos,
} from '@travel-planner/shared/utils/item-video';

// What the Preview tab of the product window shows, and nothing else: no
// private notes, suppliers, documents or creator. Built client-side by the XT
// modal and server-side for the agent portal, which only ever receives this.

export type ProductPreviewContent = {
  title: string;
  stars: number;
  /** 1–4 budget signs; 0 when unrated. */
  budgetTier: number;
  images: string[];
  /** Playable video addresses (embeds or files); shown instead of photos. */
  videos: string[];
  /** Raw rich-text HTML in the preferred language; sanitized on render. */
  descriptionHtml: string;
  /** Public (customer-facing) notes, raw HTML. */
  notesHtml: string;
  facilities: Array<{ id: string; title: string }>;
};

/** The `products.byId` fields the preview reads. */
export type ProductPreviewSource = {
  title?: unknown;
  titleEn?: string | null;
  stars?: number | string | null;
  budgetValue?: number | null;
  defaultBudgetValue?: number | null;
  description?: unknown;
  notes?: string | null;
  attachments?: unknown;
  thumbnail?: string | null;
  videos?: unknown;
  facilities?: ReadonlyArray<{
    id: string;
    title: unknown;
    isOutdoor?: boolean;
  }> | null;
};

const isLocalizedTextEntry = (
  v: unknown
): v is { languageCode: string; text: string } =>
  typeof v === 'object' &&
  v !== null &&
  'languageCode' in v &&
  'text' in v &&
  typeof (v as { text: unknown }).text === 'string';

// Legacy parity (`getValueByLanguage`): prefer the requested language
// (the host itinerary's, when the modal is opened from the planner),
// else the first available entry. Standalone /products opens with no
// language and keeps the historical 'en'-first default.
export const resolveLocalized = (
  value: unknown,
  preferLanguage?: string | null
): string => {
  if (!Array.isArray(value)) return '';
  const lang = preferLanguage ?? 'en';
  const match = value.find(
    (t): t is { languageCode: string; text: string } =>
      isLocalizedTextEntry(t) && t.languageCode === lang
  );
  if (match?.text) return match.text;
  return value.find(isLocalizedTextEntry)?.text ?? '';
};

const resolveAttachmentUrl = (raw: Record<string, unknown>): string => {
  if (typeof raw.url === 'string' && raw.url) return raw.url;
  if (typeof raw.fileKey === 'string' && raw.fileKey) {
    return resolveThumbnailUrl(raw.fileKey) ?? '';
  }
  return '';
};

// Image attachments (legacy filters `type === IMAGE`, tolerates a missing
// type); falls back to the thumbnail when there are none.
export const resolveImageList = (
  data: ProductPreviewSource | null | undefined
): string[] => {
  const out: string[] = [];
  if (Array.isArray(data?.attachments)) {
    for (const raw of data.attachments) {
      if (!raw || typeof raw !== 'object') continue;
      const r = raw as Record<string, unknown>;
      const type = typeof r.type === 'string' ? r.type.toUpperCase() : null;
      if (type && type !== 'IMAGE') continue;
      const url = resolveAttachmentUrl(r);
      if (url) out.push(url);
    }
  }
  if (out.length === 0) {
    const thumb = resolveThumbnailUrl(data?.thumbnail ?? null);
    if (thumb) out.push(thumb);
  }
  return out;
};

export const toProductPreviewContent = (
  data: ProductPreviewSource | null | undefined,
  languageCode?: string | null
): ProductPreviewContent => ({
  title:
    resolveLocalized(data?.title, languageCode) || (data?.titleEn ?? 'Product'),
  stars: Number(data?.stars ?? 0),
  budgetTier: data?.budgetValue ?? data?.defaultBudgetValue ?? 0,
  images: resolveImageList(data),
  videos: narrowVideos(data?.videos).map(getVideoEmbedUrl),
  descriptionHtml: resolveLocalized(data?.description, languageCode),
  notesHtml: data?.notes ?? '',
  facilities: (data?.facilities ?? [])
    .map((facility) => ({
      id: facility.id,
      title: resolveLocalized(facility.title, languageCode),
    }))
    .filter((facility) => facility.title),
});

/**
 * How a product video plays in the window: a file in a `<video>`, a
 * YouTube / Vimeo / Wistia link in an iframe asked to start at once, muted
 * (browsers only let a muted video start by itself).
 */
export const playableVideo = (
  url: string
): { kind: 'file' | 'embed'; src: string } => {
  if (isDirectMediaUrl(url)) return { kind: 'file', src: url };
  const add = (params: Record<string, string>) => {
    const next = new URL(url);
    for (const [key, value] of Object.entries(params)) {
      next.searchParams.set(key, value);
    }
    return next.toString();
  };
  try {
    if (url.includes('youtube.com/embed/')) {
      return {
        kind: 'embed',
        src: add({ autoplay: '1', mute: '1', playsinline: '1' }),
      };
    }
    if (url.includes('player.vimeo.com')) {
      return { kind: 'embed', src: add({ autoplay: '1', muted: '1' }) };
    }
    if (url.includes('wistia.net')) {
      return { kind: 'embed', src: add({ autoPlay: 'true', muted: 'true' }) };
    }
  } catch {
    // Not a URL we can extend: play it as given.
  }
  return { kind: 'embed', src: url };
};
